import React, { useState } from "react";
import { Icon, SegmentedControl } from "stories/Admin";
import { CampaignFormValues, formatKr } from "./campaignKind";
import {
  CampaignPreviewModel,
  PreviewProgress,
  campaignPreview,
  payLabel,
} from "./previewModel";
import styles from "./CampaignPreview.module.scss";

/*
 * Forhåndsvisning av kampanjen slik den ser ut i appen (Min moské), med
 * samme farger og former som appens designsystem. Oppdateres mens admin skriver.
 */

type View = "card" | "tap";

const VIEWS: Array<{ value: View; label: string }> = [
  { value: "card", label: "Kort" },
  { value: "tap", label: "Etter trykk" },
];

const RING_R = 40;
const RING_C = 2 * Math.PI * RING_R;

const SampleTag: React.FC = () => (
  <span className={styles.sampleTag}>Eksempel</span>
);

/** Liten stolpe + tekst på kortet. */
const CardProgress: React.FC<{ p: PreviewProgress }> = ({ p }) => (
  <div className={styles.cardProgress}>
    {p.targetKr > 0 && (
      <span className={styles.bar} aria-hidden="true">
        <span key={p.percent} style={{ width: `${p.percent}%` }} />
      </span>
    )}
    <span className={styles.cardProgressText}>
      {p.label}
      {p.sample && <SampleTag />}
    </span>
  </div>
);

/** Ringen på giverskjermen; fylles fra 0 hver gang tallene endres. */
const ProgressHero: React.FC<{ p: PreviewProgress }> = ({ p }) => (
  <div className={styles.hero}>
    {p.targetKr > 0 ? (
      <svg
        className={styles.ring}
        viewBox="0 0 96 96"
        width={84}
        height={84}
        aria-hidden="true"
      >
        <circle className={styles.ringBg} cx="48" cy="48" r={RING_R} />
        <circle
          key={p.percent}
          className={styles.ringFg}
          cx="48"
          cy="48"
          r={RING_R}
          strokeDasharray={RING_C}
          style={
            {
              "--ring-c": RING_C,
              "--ring-to": RING_C * (1 - p.percent / 100),
            } as React.CSSProperties
          }
        />
        <text x="48" y="54" textAnchor="middle" className={styles.ringText}>
          {p.percent} %
        </text>
      </svg>
    ) : (
      <span className={styles.heroIcon} aria-hidden="true">
        <Icon name="heart" size={24} />
      </span>
    )}
    <div className={styles.heroText}>
      <b>{p.raisedLabel}</b>
      <span>{p.ofLabel}</span>
      <span className={styles.heroDonors}>
        <Icon name="users" size={13} strokeWidth={2.2} />
        {p.donorCount === 1 ? "1 giver" : `${p.donorCount} givere`}
      </span>
    </div>
    {p.sample && <SampleTag />}
  </div>
);

/** Kampanjekortet på Min moské. */
const CampaignCard: React.FC<{ m: CampaignPreviewModel; dim?: boolean }> = ({
  m,
  dim,
}) => (
  <div className={`${styles.card} ${dim ? styles.dim : ""}`}>
    <span className={styles.tile} aria-hidden="true">
      <Icon name="heart" size={20} />
    </span>
    <div className={styles.cardBody}>
      <b className={styles.cardTitle}>{m.name}</b>
      <span className={styles.cardDesc}>{m.description}</span>
      {m.progress && <CardProgress p={m.progress} />}
    </div>
    {m.trailing === "give" && <span className={styles.giPill}>Gi</span>}
    {m.trailing === "open" && (
      <span className={styles.trailIcon} aria-label="Åpner lenke">
        ↗
      </span>
    )}
    {m.trailing === "info" && (
      <span className={styles.trailIcon} aria-hidden="true">
        <Icon name="chevronRight" size={16} strokeWidth={2.4} />
      </span>
    )}
  </div>
);

/** «Min moské» med kortet under «Innsamlinger». */
const HomeScreen: React.FC<{
  m: CampaignPreviewModel;
  mosqueName?: string | null;
  dim?: boolean;
}> = ({ m, mosqueName, dim }) => (
  <div className={styles.home}>
    <div className={`${styles.homeTop} ${dim ? styles.dim : ""}`}>
      <span className={styles.homeMosque}>{mosqueName || "Min moské"}</span>
      <span className={styles.ghost} />
      <span className={`${styles.ghost} ${styles.ghostShort}`} />
    </div>
    <span className={`${styles.section} ${dim ? styles.dim : ""}`}>
      Innsamlinger
    </span>
    <CampaignCard m={m} dim={dim} />
  </div>
);

/** Giverskjermen (bunnark) for gaver i appen. */
const GiveSheet: React.FC<{ m: CampaignPreviewModel }> = ({ m }) => {
  const [interval, setGiftInterval] = useState<"ONE_TIME" | "MONTHLY">(
    m.intervals[0],
  );
  const [picked, setPicked] = useState<number | null>(null);
  const current = m.intervals.includes(interval) ? interval : m.intervals[0];
  const amount =
    picked != null && m.amounts.includes(picked)
      ? picked
      : (m.amounts[1] ?? m.amounts[0]);
  const monthly = current === "MONTHLY";
  const [category, setCategory] = useState<string | null>(null);
  // Valget gjelder bare så lenge kategorien fortsatt finnes i skjemaet.
  const chosen = category && m.categories.includes(category) ? category : null;
  const needsCategory = m.categories.length > 0 && !chosen;
  const payClass = `${styles.payBtn} ${needsCategory ? styles.payOff : ""}`;
  return (
    <>
      <b className={styles.sheetTitle}>{m.name}</b>
      <p className={styles.sheetDesc}>{m.description}</p>
      {m.progress && <ProgressHero p={m.progress} />}
      {m.intervals.length > 1 && (
        <div className={styles.tabs} role="tablist" aria-label="Type gave">
          {m.intervals.map((i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={current === i}
              className={current === i ? styles.tabOn : undefined}
              onClick={() => setGiftInterval(i)}
            >
              {i === "ONE_TIME" ? "Engangs" : "Månedlig"}
            </button>
          ))}
        </div>
      )}
      {m.categories.length > 0 && (
        <>
          <span className={styles.eyebrow}>Velg kategori</span>
          <div
            className={styles.categoryButtons}
            role="radiogroup"
            aria-label="Velg kategori"
          >
            {m.categories.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={chosen === c}
                className={chosen === c ? styles.categoryOn : undefined}
                onClick={() => setCategory(c)}
              >
                <span className={styles.radioDot} aria-hidden="true" />
                {c}
              </button>
            ))}
          </div>
        </>
      )}
      <span className={styles.eyebrow}>
        {monthly ? "Beløp per måned" : "Beløp"}
      </span>
      <div className={styles.chips}>
        {m.amounts.map((a) => (
          <button
            key={a}
            type="button"
            className={a === amount ? styles.chipOn : undefined}
            onClick={() => setPicked(a)}
          >
            {formatKr(a)}
          </button>
        ))}
      </div>
      {m.providers.includes("VIPPS") && (
        <span
          className={`${payClass} ${styles.vipps}`}
          aria-disabled={needsCategory || undefined}
        >
          {payLabel("VIPPS", amount, chosen)}
        </span>
      )}
      {m.providers.includes("STRIPE") && (
        <span
          className={`${payClass} ${styles.cardBtn}`}
          aria-disabled={needsCategory || undefined}
        >
          {payLabel("STRIPE", amount, chosen)}
        </span>
      )}
      {needsCategory && m.providers.length > 0 && (
        <span className={styles.categoryHint}>
          Velg en kategori for å fortsette
        </span>
      )}
      {m.providers.length === 0 ? (
        <span className={styles.sheetNote}>
          Velg Vipps eller kort, så vises gi-knappen her.
        </span>
      ) : (
        <span className={styles.sheetNote}>
          {monthly
            ? "Giveren godkjenner den faste gaven hos Vipps eller Stripe. Pengene går rett til moskeen."
            : "Giveren betaler hos Vipps eller Stripe. Pengene går rett til moskeen."}
        </span>
      )}
    </>
  );
};

/** Detaljene for «Bare informasjon»: ingen gi-knapp. */
const InfoDetail: React.FC<{
  m: CampaignPreviewModel;
  mosqueName?: string | null;
}> = ({ m, mosqueName }) => (
  <div className={styles.detail}>
    <div className={styles.detailHeader} aria-hidden="true">
      <Icon name="heart" size={34} />
    </div>
    <div className={styles.detailSheet}>
      <span className={styles.goldPill}>INNSAMLING</span>
      <b className={styles.detailTitle}>{m.name}</b>
      {mosqueName && <span className={styles.detailMosque}>{mosqueName}</span>}
      {m.targetKr > 0 && (
        <div className={styles.goal}>
          <span className={styles.eyebrow}>Mål</span>
          <b>{formatKr(m.targetKr)}</b>
        </div>
      )}
      <p className={styles.detailDesc}>{m.description}</p>
      <span className={styles.sheetNote}>
        Ingen gi-knapp: bare informasjon.
      </span>
    </div>
  </div>
);

interface Props {
  values: CampaignFormValues;
  /** Ekte tall for en lagret kampanje (brukes i stedet for eksempeltall). */
  real?: { raisedOre?: number | null; donorCount?: number | null } | null;
  mosqueName?: string | null;
}

const CampaignPreview: React.FC<Props> = ({ values, real, mosqueName }) => {
  const [view, setView] = useState<View>("card");
  const m = campaignPreview(values, real);

  const screen = () => {
    if (view === "card") return <HomeScreen m={m} mosqueName={mosqueName} />;
    if (m.mode === "LINK") {
      return (
        <>
          <HomeScreen m={m} mosqueName={mosqueName} />
          <div className={styles.linkNote}>
            <span className={styles.linkIcon} aria-hidden="true">
              <Icon name="link" size={16} />
            </span>
            <span>
              Åpner lenken direkte (f.eks. Vipps)
              <b>{m.linkHost ?? "Ingen gyldig lenke ennå"}</b>
            </span>
          </div>
        </>
      );
    }
    if (m.mode === "INFO") {
      return <InfoDetail m={m} mosqueName={mosqueName} />;
    }
    return (
      <>
        <HomeScreen m={m} mosqueName={mosqueName} dim />
        <div className={styles.backdrop} />
        <div className={styles.sheet}>
          <span className={styles.grabber} aria-hidden="true" />
          <GiveSheet m={m} />
        </div>
      </>
    );
  };

  return (
    <aside className={styles.preview} aria-label="Forhåndsvisning i appen">
      <div className={styles.head}>
        <span className={styles.headTitle}>Slik ser det ut i appen</span>
        <SegmentedControl
          label="Forhåndsvisning"
          options={VIEWS}
          value={view}
          onChange={setView}
        />
      </div>
      {values.collectionMode ? (
        <div className={styles.phone}>
          <div className={styles.screen}>{screen()}</div>
        </div>
      ) : (
        <div className={styles.empty}>
          Velg hva slags kampanje, så ser du den her.
        </div>
      )}
    </aside>
  );
};

export default CampaignPreview;
